import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from "../components/ui/sheet";
import { Textarea } from "../components/ui/textarea";
import { AmountInput } from "../components/ui/amount-input";
import { PiggyBank, Plus, Banknote, FileDown, ArrowDownCircle, CheckCircle2, XCircle, Clock, Wallet } from "lucide-react";
import { formatINR, formatDate, formatDateTime, todayISO, fileToBase64, displayAcCode } from "../lib/format";
import { openAuthDownload } from "../lib/download";
import { toast } from "sonner";

export default function Pigmy() {
    const { t } = useLang();
    const { user } = useAuth();
    const nav = useNavigate();
    const [accounts, setAccounts] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [openCreate, setOpenCreate] = useState(false);
    const [form, setForm] = useState({
        customer_id: "",
        frequency: "daily",
        installment_amount: 100,
        tenure_months: 12,
        start_date: todayISO(),
        notes: "",
    });

    async function load() {
        const { data } = await api.get("/pigmy/accounts");
        setAccounts(data);
    }
    async function approve(aid, approveAction) {
        const reason = approveAction ? "" : (prompt("Rejection reason:") ?? "");
        try {
            await api.post(`/pigmy/accounts/${aid}/approve`, { approve: approveAction, reason });
            toast.success(approveAction ? "Approved" : "Rejected");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }
    useEffect(() => {
        load();
        api.get("/customers").then((r) => setCustomers(r.data));
    }, []);

    async function createAccount() {
        if (!form.customer_id) return toast.error("Select a customer");
        try {
            await api.post("/pigmy/accounts", form);
            toast.success("Saving Scheme account opened");
            setOpenCreate(false);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    const isAdmin = user?.role === "admin";
    const pending = accounts.filter((a) => a.status === "pending");
    return (
        <div className="space-y-5">
            {isAdmin && <WithdrawRequestsPanel />}
            {isAdmin && pending.length > 0 && (
                <Card
                    className="rounded-2xl border-amber-200 bg-amber-50 p-4"
                    data-testid="pigmy-pending-section"
                >
                    <div className="text-[10px] uppercase tracking-wider font-bold text-amber-800">
                        Saving Scheme approvals pending ({pending.length})
                    </div>
                    <div className="mt-2 divide-y divide-amber-100">
                        {pending.map((a) => (
                            <div
                                key={a.id}
                                className="py-2 flex items-center justify-between gap-3"
                                data-testid={`pigmy-pending-${a.id}`}
                            >
                                <div className="min-w-0">
                                    <div className="font-semibold text-slate-900 truncate">
                                        {displayAcCode(a.account_code)} · {a.customer_id?.slice(0, 8)}…
                                    </div>
                                    <div className="text-xs text-slate-600">
                                        ₹{a.installment_amount} {a.frequency} · {a.tenure_months}mo · by {a.created_by}
                                    </div>
                                </div>
                                <div className="flex gap-1.5">
                                    <Button
                                        size="sm"
                                        onClick={() => approve(a.id, true)}
                                        data-testid={`pigmy-approve-${a.id}`}
                                        className="h-9 rounded-lg bg-emerald-600 hover:bg-emerald-700"
                                    >
                                        Approve
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => approve(a.id, false)}
                                        data-testid={`pigmy-reject-${a.id}`}
                                        className="h-9 rounded-lg text-rose-700"
                                    >
                                        Reject
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            )}
            <div className="flex items-center justify-between">
                <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">
                    Saving Scheme Accounts
                </h1>
                <Dialog open={openCreate} onOpenChange={setOpenCreate}>
                    <DialogTrigger asChild>
                        <Button data-testid="new-pigmy-btn" className="h-11 rounded-xl bg-blue-900 hover:bg-blue-800 tap">
                            <Plus className="h-4 w-4 mr-1.5" />
                            New Saving Scheme A/C
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="rounded-2xl">
                        <DialogHeader>
                            <DialogTitle>Open Saving Scheme Account</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-3">
                            <div>
                                <Label>Customer</Label>
                                <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                                    <SelectTrigger className="h-11 rounded-xl" data-testid="pigmy-customer-select">
                                        <SelectValue placeholder="Select customer" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {customers.map((c) => (
                                            <SelectItem key={c.id} value={c.id}>
                                                {c.name} · {c.customer_code}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Frequency</Label>
                                    <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                                        <SelectTrigger className="h-11 rounded-xl" data-testid="pigmy-frequency">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="daily">Daily</SelectItem>
                                            <SelectItem value="weekly">Weekly</SelectItem>
                                            <SelectItem value="monthly">Monthly</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Installment (₹)</Label>
                                    <AmountInput data-testid="pigmy-installment" value={form.installment_amount}
                                        onChange={(v) => setForm({ ...form, installment_amount: v })}
                                        className="h-11 rounded-xl" />
                                </div>
                                <div>
                                    <Label>Tenure (months)</Label>
                                    <AmountInput data-testid="pigmy-tenure" value={form.tenure_months}
                                        onChange={(v) => setForm({ ...form, tenure_months: v })}
                                        className="h-11 rounded-xl" />
                                </div>
                                <div>
                                    <Label>Start Date</Label>
                                    <Input data-testid="pigmy-start" type="date" value={form.start_date}
                                        onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                                        className="h-11 rounded-xl" />
                                </div>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button onClick={createAccount} data-testid="save-pigmy-btn" className="bg-blue-900 hover:bg-blue-800">
                                {t("save")}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            {accounts.length === 0 ? (
                <Card className="rounded-2xl border-slate-100 p-8 text-center text-sm text-slate-400">
                    <PiggyBank className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                    No Saving Scheme accounts yet
                </Card>
            ) : (
                <div className="grid sm:grid-cols-2 gap-3">
                    {accounts.map((a) => (
                        <Link key={a.id} to={`/pigmy/${a.id}`} data-testid={`pigmy-row-${a.id}`}
                            className="block">
                            <Card className="rounded-2xl border-slate-100 p-4 card-hover">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <div className="font-semibold text-slate-900">{a.customer_name}</div>
                                            <span className="text-[10px] font-mono uppercase bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                                                {displayAcCode(a.account_code)}
                                            </span>
                                        </div>
                                        <div className="text-xs text-slate-500 mt-0.5 capitalize">
                                            {a.frequency} · {formatINR(a.installment_amount)} · ends {formatDate(a.maturity_date)}
                                        </div>
                                    </div>
                                    <Badge className={a.status === "active"
                                        ? "bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-50"
                                        : "bg-slate-100 text-slate-700"}>
                                        {a.status}
                                    </Badge>
                                </div>
                                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Balance</div>
                                    <div className="num font-bold text-blue-900 text-lg">{formatINR(a.balance)}</div>
                                </div>
                            </Card>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}


/* ============================================================ */
/* Admin: Saving Scheme Withdrawal Requests queue                */
/* ============================================================ */
function WithdrawRequestsPanel() {
    const [tab, setTab] = useState("pending");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState("");

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get("/pigmy/withdraw-requests", {
                params: { status: tab },
            });
            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        load();
    }, [tab]);

    async function act(kind, r) {
        const label = kind === "approve" ? "Approve" : kind === "reject" ? "Reject" : "Mark Paid";
        if (!window.confirm(`${label} withdrawal of ${formatINR(r.principal)} for ${r.customer_name || r.customer_code}?`)) return;
        setBusy(r.id + kind);
        try {
            await api.post(`/pigmy/withdraw-requests/${kind}`, { request_id: r.id });
            toast.success(`${label} succeeded`);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy("");
        }
    }

    const tabs = [
        { key: "pending",  label: "Pending",  icon: Clock,        tone: "amber" },
        { key: "approved", label: "Approved", icon: CheckCircle2, tone: "emerald" },
        { key: "paid",     label: "Paid",     icon: Wallet,       tone: "blue" },
        { key: "rejected", label: "Rejected", icon: XCircle,      tone: "rose" },
    ];

    const statusBadge = (s) => {
        const map = {
            pending:  "bg-amber-50 text-amber-800 border-amber-100",
            approved: "bg-emerald-50 text-emerald-700 border-emerald-100",
            paid:     "bg-blue-50 text-blue-800 border-blue-100",
            rejected: "bg-rose-50 text-rose-700 border-rose-100",
        };
        return (
            <Badge className={`${map[s] || "bg-slate-100 text-slate-700"} hover:opacity-100 capitalize`}>
                {s}
            </Badge>
        );
    };

    return (
        <Card className="rounded-2xl border-slate-100 p-4 sm:p-5" data-testid="withdraw-requests-panel">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-brand-soft/70 grid place-items-center">
                        <ArrowDownCircle className="h-5 w-5 text-brand-royal" strokeWidth={1.75} />
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-brand-royal font-semibold">
                            Saving Scheme
                        </div>
                        <h3 className="mt-0.5 font-display font-bold text-slate-900 text-lg">
                            Withdrawal Requests
                        </h3>
                    </div>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={load}
                    className="h-9 rounded-lg"
                    data-testid="withdraw-refresh-btn"
                >
                    Refresh
                </Button>
            </div>

            {/* Tabs */}
            <div className="mt-4 flex flex-wrap gap-1.5" data-testid="withdraw-tabs">
                {tabs.map((t) => {
                    const active = tab === t.key;
                    const Icon = t.icon;
                    return (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            className={`h-9 rounded-full px-3 text-xs font-semibold border transition inline-flex items-center gap-1.5 ${
                                active
                                    ? "bg-brand-navy text-white border-brand-navy"
                                    : "bg-white text-slate-700 border-slate-200 hover:border-brand-royal"
                            }`}
                            data-testid={`withdraw-tab-${t.key}`}
                        >
                            <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
                            {t.label}
                        </button>
                    );
                })}
            </div>

            {/* List */}
            <div className="mt-4">
                {loading ? (
                    <div className="p-8 text-center text-sm text-slate-400">Loading…</div>
                ) : rows.length === 0 ? (
                    <div className="p-8 text-center text-sm text-slate-400"
                        data-testid="withdraw-empty">
                        No {tab} withdrawal requests.
                    </div>
                ) : (
                    <div className="divide-y divide-slate-100 rounded-xl border border-slate-100 overflow-hidden">
                        {rows.map((r) => (
                            <div key={r.id}
                                className="p-3 sm:p-4 flex flex-wrap items-start justify-between gap-3"
                                data-testid={`withdraw-row-${r.id}`}
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <div className="font-semibold text-slate-900 truncate">
                                            {r.customer_name || "—"}
                                        </div>
                                        <span className="text-[10px] font-mono uppercase bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                                            {r.customer_code || ""}
                                        </span>
                                        <span className="text-[10px] font-mono uppercase bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                                            A/C {displayAcCode(r.account_code) || "—"}
                                        </span>
                                        {statusBadge(r.status)}
                                        {r.tier && (
                                            <Badge className="bg-brand-soft text-brand-royal border-brand-soft hover:bg-brand-soft">
                                                Tier {r.tier}
                                            </Badge>
                                        )}
                                    </div>
                                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                                        <MiniStat label="Principal" value={formatINR(r.principal)} />
                                        <MiniStat label={`Interest (${r.applicable_rate ?? 0}%)`}
                                            value={formatINR(r.interest)} />
                                        <MiniStat label="Penalty" value={formatINR(r.penalty)} />
                                        <MiniStat label="Net Payout"
                                            value={formatINR(r.net_payout)}
                                            highlight />
                                    </div>
                                    <div className="mt-2 text-[11px] text-slate-500">
                                        Requested {r.request_date ? formatDate(r.request_date) : "—"}
                                        {r.approved_at ? ` · Approved ${formatDateTime(r.approved_at)}` : ""}
                                        {r.paid_at ? ` · Paid ${formatDateTime(r.paid_at)}` : ""}
                                        {r.rejected_at ? ` · Rejected ${formatDateTime(r.rejected_at)}` : ""}
                                        {typeof r.current_balance === "number"
                                            ? ` · Current bal ${formatINR(r.current_balance)}`
                                            : ""}
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-1.5 shrink-0">
                                    {r.status === "pending" && (
                                        <>
                                            <Button
                                                size="sm"
                                                onClick={() => act("approve", r)}
                                                disabled={busy === r.id + "approve"}
                                                className="h-9 rounded-lg bg-emerald-600 hover:bg-emerald-700"
                                                data-testid={`withdraw-approve-${r.id}`}
                                            >
                                                Approve
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => act("reject", r)}
                                                disabled={busy === r.id + "reject"}
                                                className="h-9 rounded-lg text-rose-700"
                                                data-testid={`withdraw-reject-${r.id}`}
                                            >
                                                Reject
                                            </Button>
                                        </>
                                    )}
                                    {r.status === "approved" && (
                                        <>
                                            <Button
                                                size="sm"
                                                onClick={() => act("mark-paid", r)}
                                                disabled={busy === r.id + "mark-paid"}
                                                className="h-9 rounded-lg bg-blue-900 hover:bg-blue-800"
                                                data-testid={`withdraw-mark-paid-${r.id}`}
                                            >
                                                Mark Paid
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => act("reject", r)}
                                                disabled={busy === r.id + "reject"}
                                                className="h-9 rounded-lg text-rose-700"
                                                data-testid={`withdraw-reject-${r.id}`}
                                            >
                                                Reject
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </Card>
    );
}

function MiniStat({ label, value, highlight }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                {label}
            </div>
            <div className={`num mt-0.5 text-sm font-bold ${highlight ? "text-emerald-700" : "text-slate-900"}`}>
                {value}
            </div>
        </div>
    );
}
